import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Step1Welcome } from '@/components/onboarding/Step1Welcome'
import { Step2Accounts } from '@/components/onboarding/Step2Accounts'
import { Step3Positions } from '@/components/onboarding/Step3Positions'
import { Step4SavingPlans } from '@/components/onboarding/Step4SavingPlans'
import { Step5ExternalAssets } from '@/components/onboarding/Step5ExternalAssets'
import { Step6Strategy } from '@/components/onboarding/Step6Strategy'
import { StepIndicator } from '@/components/onboarding/StepIndicator'
import { WizardNav } from '@/components/onboarding/WizardNav'
import { listAccounts } from '@/lib/accountsApi'
import { TOTAL_STEPS, useOnboardingStore } from '@/stores/onboarding'

export function OnboardingPage() {
  const navigate = useNavigate()
  const currentStep = useOnboardingStore((s) => s.currentStep)
  const stepStatus = useOnboardingStore((s) => s.stepStatus)
  const goNext = useOnboardingStore((s) => s.goNext)
  const goPrev = useOnboardingStore((s) => s.goPrev)
  const markCompleted = useOnboardingStore((s) => s.markCompleted)
  const markSkipped = useOnboardingStore((s) => s.markSkipped)
  const dismiss = useOnboardingStore((s) => s.dismiss)

  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: listAccounts })
  const accountCount = accountsQuery.data?.length ?? 0

  const isLast = currentStep === TOTAL_STEPS
  const nextDisabled = currentStep === 2 && accountCount === 0

  const handleNext = () => {
    markCompleted(currentStep)
    goNext()
  }

  const handleSkip = () => {
    markSkipped(currentStep)
    goNext()
  }

  const handleFinish = () => {
    markCompleted(currentStep)
    navigate('/dashboard')
  }

  const handleDismiss = () => {
    dismiss()
    navigate('/dashboard')
  }

  return (
    <div className="flex min-h-screen flex-col bg-void text-ink">
      <header className="hairline-b flex flex-col gap-3 bg-panel px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
        <div>
          <p className="tracked-caps text-[10px] text-faint">Initial-Setup</p>
          <h1 className="font-mono text-sm text-ink">Onboarding-Wizard</h1>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="self-end font-mono text-xs text-muted transition-colors hover:text-bernstein md:self-auto"
        >
          Wizard verlassen →
        </button>
      </header>

      <div className="hairline-b bg-panel px-4 py-3 md:px-6">
        <StepIndicator currentStep={currentStep} stepStatus={stepStatus} />
      </div>

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-8 md:px-6 md:py-10">
        <p className="tracked-caps mb-6 text-[10px] text-faint">
          Schritt {currentStep} / {TOTAL_STEPS}
        </p>

        {currentStep === 1 && <Step1Welcome />}
        {currentStep === 2 && <Step2Accounts />}
        {currentStep === 3 && <Step3Positions />}
        {currentStep === 4 && <Step4SavingPlans />}
        {currentStep === 5 && <Step5ExternalAssets />}
        {currentStep === 6 && <Step6Strategy />}

        <WizardNav
          onPrev={goPrev}
          onNext={handleNext}
          onSkip={handleSkip}
          onFinish={handleFinish}
          prevDisabled={currentStep === 1}
          nextDisabled={nextDisabled}
          showSkip={currentStep > 2 && currentStep < TOTAL_STEPS}
          isLast={isLast}
          hideNext={currentStep === TOTAL_STEPS}
        />
      </main>
    </div>
  )
}
